// Copyright (c) 2019 Comsearch, Inc., All rights reserved

import { useEffect, useRef, useState } from 'react'
import { AnalysisStatusIdEnum } from 'config/enums'
import { useInterval } from 'hooks'
import getDuration from 'helpers/getDuration'

// startTime and endTime are in epoch milli.
// this will return an elapsed time label
// in YYYY:MM:DD HH:mm:ssa format (via momentjs)
// if endTime === null, then this will create an
// updated label every second. if endTime !== null,
// then it will be static.

const useElapsedTime = (startTime, endTime, analysisStatusId) => {
  // if there's a start time but no end time, then the delay is at 1s
  // otherwise, we don't need a delay as we're not counting anything.
  const getElapsedTime = () => (
    startTime !== null &&
    endTime === null &&
    analysisStatusId !== AnalysisStatusIdEnum.FAILED
      ? 1000
      : null
  )

  // this is the actual string which is rendered in the component.
  // it will be either "-" when an analysis has not been run
  // otherwise it will be HH:MM:SS
  const [elapsedTime, updateElapsedTime] = useState(() => getDuration(startTime, endTime))

  const callback = () => updateElapsedTime(() => getDuration(startTime, endTime))

  // store the callback as a ref. This way, useInterval can depend that the
  // callback will be the same every time and it wont unnecessarily reset the setInterval
  const callbackRef = useRef(callback)

  // determine the intervalDuration based on the endTime of the analysis.
  // if it's complete, the interval will not fire.
  const [intervalDuration, updateIntervalDuration] = useState(getElapsedTime)

  useEffect(() => {
    callbackRef.current = callback

    const newDuration = getElapsedTime()

    // handle s
    if (analysisStatusId === AnalysisStatusIdEnum.FAILED) {
      updateElapsedTime(() => '-')
      updateIntervalDuration(() => null)
    } else {
      // if there's a change here, store it
      if (newDuration !== intervalDuration) {
        updateIntervalDuration(() => newDuration)
      }

      // if the new duration will be null, then run getDuration
      // once more so we know we have the true duration value
      if (newDuration === null) {
        updateElapsedTime(() => getDuration(startTime, endTime || Date.now()))
      }
    }
  }, [startTime, endTime, analysisStatusId])

  useInterval(callbackRef.current, intervalDuration)

  return elapsedTime
}

export default useElapsedTime
